import ImageIcon from "@mui/icons-material/Image";
import { GridColDef } from "@mui/x-data-grid-pro";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DateCell } from "@/components/grid/cells/DateCell";
import { MaintenanceReferenceCell } from "@/components/grid/cells/MaintenanceReferenceCell";
import MaintenanceStatusCodeCell from "@/components/grid/cells/MaintenanceStatusCodeCell";
import { NameCell } from "@/components/grid/cells/NameCell";
import WorkOrderCell from "@/components/grid/cells/WorkOrderCell";
import { MaintenanceDisplayData } from "@/models/MaintenanceDisplayData";
import { STATUS_MAP, StatusCode } from "@/models/MaintenanceWorkOrderJob";

export default function useMaintenanceColumns(minimal: boolean = false) {
  const { t } = useTranslation();

  const initialColumns: GridColDef[] = useMemo(() => {
    const allColumns: GridColDef[] = [
      {
        minWidth: 200,
        field: "device.name",
        headerName: t("faultOverview.deviceName", "Device Name"),
        renderCell: NameCell,
        valueGetter: (_, row) => row?.device?.name ?? "---",
      },
      {
        minWidth: 150,
        field: "source",
        headerName: t("maintenance.source", "Source"),
        renderCell: NameCell,
      },
      {
        width: 180,
        field: "openedBy.name",
        headerName: t("maintenance.openedBy", "Opened by"),
        renderCell: NameCell,
        valueGetter: (_, row) => row?.openedBy?.name ?? "---",
      },
      {
        minWidth: 10,
        width: 150,
        field: "createdAt",
        type: "dateTime",
        headerName: t("maintenance.createdAt", "Created at"),
        renderCell: DateCell,
      },
      {
        minWidth: 10,
        width: 300,
        field: "name",
        headerName: t("maintenance.name", "Name"),
        renderCell: WorkOrderCell,
        valueGetter: (_, row: MaintenanceDisplayData) => row?.workOrderJob?.maintenanceType?.name,
      },
      {
        minWidth: 10,
        width: 200,
        flex: 1,
        field: "workOrderJob",
        headerName: t("maintenance.eventDescription", "Description"),
        renderCell: NameCell,
        valueGetter: (_, row: MaintenanceDisplayData) => row?.workOrderJob?.description ?? "---",
      },
      {
        minWidth: 10,
        width: 150,
        field: "statusCode",
        headerName: t("maintenance.statusCode", "Status"),
        renderCell: MaintenanceStatusCodeCell,
        editable: true,
        type: "singleSelect",
        valueFormatter: value => t(`maintenance.status.${STATUS_MAP[value as StatusCode]}`),
        valueOptions: Object.entries(STATUS_MAP).map(([value, label]) => ({
          value: Number(value),
          label: t(`maintenance.status.${label}`),
        })),
      },
      {
        minWidth: 10,
        width: 80,
        field: "appendix",
        headerName: t("maintenance.appendix"),
        type: "boolean",
        valueGetter: (_, row: MaintenanceDisplayData) =>
          (row?.workOrderJob?.mediaFiles?.length ?? 0) > 0,
        renderCell: params => {
          const hasMedia = params.value;
          return hasMedia ? <ImageIcon /> : null;
        },
      },
      {
        minWidth: 10,
        width: 150,
        field: "updatedAt",
        type: "dateTime",
        headerName: t("maintenance.updatedAt", "Updated at"),
        renderCell: DateCell,
      },
      {
        minWidth: 10,
        width: 100,
        field: "reference",
        headerName: t("maintenance.reference", "Reference"),
        renderCell: MaintenanceReferenceCell,
      },
    ];

    if (minimal) {
      // Only keep specific columns when minimal mode is true
      return allColumns.filter(col =>
        ["openedBy.name", "createdAt", "name", "workOrderJob", "statusCode"].includes(col.field),
      );
    }

    return allColumns;
  }, [t, minimal]);

  return { initialColumns };
}
